import { useSelector } from '@xstate/vue';
import { ComputedRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouteLocationMatched, RouteRecordRaw, useRouter } from 'vue-router';

import { NavigationEntry } from '@silae/components';
import { useLocalBoolean } from '@silae/composables';
import { getValue } from '@silae/helpers';
import { startNavigationService } from '~/_v3/common/services/navigationService';
import { getToken } from '~/_v3/composables/useToken';

import { routes } from '../router';

const appName = import.meta.env.SILAE_APP_NAME;
const { value: isCollapsed, toggle: toggleCollapse } = useLocalBoolean(`${appName}-collapsed-menu`);

export { isCollapsed, toggleCollapse };

type NavigationEntriesOptions = {
	filterByNavEntry?: boolean;
	filterBySettingsNavEntry?: boolean;
};

export function useNavigationEntries(options: NavigationEntriesOptions = { filterByNavEntry: true }): {
	entries: ComputedRef<Array<NavigationEntry>>;
} {
	const { t } = useI18n();
	const router = useRouter();
	const currentRoute = computed(() => router.currentRoute.value);
	const activeRoutes = computed(() => currentRoute.value?.matched ?? []);

	const isNavEntry = (route: RouteRecordRaw) =>
		route.meta?.requiresAuth &&
		((options.filterByNavEntry && getValue(route.meta?.isNavEntry)) ||
			(options.filterBySettingsNavEntry && route.meta?.isSettingsNavEntry));

	const isSubEntry = (route: RouteRecordRaw) => route.meta?.requiresAuth && getValue(route.meta?.isSubEntry) && route.name != undefined;

	const asNavigationEntry = (route: RouteRecordRaw, activeRoutes: Array<RouteLocationMatched>): NavigationEntry => ({
		name: route.name as string,
		label: () => {
			if (route.meta?.label != undefined) {
				const val = getValue(route.meta.label);
				return t(val) ?? '';
			}
			return '';
		},
		icon: route.meta?.icon,
		active: activeRoutes.some(it => it.name === route.name),
		children: route?.children?.filter(isSubEntry)?.map(child => asNavigationEntry(child, activeRoutes)),
		to: route
	});

	const accountRoutes = activeRoutes.value.find(route => route.name === 'account');
	let isDematAgreementAllowed: (route: RouteRecordRaw) => any;
	let isFranceConnectUser: (route: RouteRecordRaw) => any;

	if (accountRoutes) {
		// we start the navigation service to check if user can select demat agreement
		const service = startNavigationService();
		const canSelectAgreement = useSelector(service, state => state.context.canSelectAgreement);
		// we keep the demat menu only if user can select demat agreement
		isDematAgreementAllowed = (route: RouteRecordRaw) => (route.meta?.requireDematAgreementRight ? canSelectAgreement.value : true);
		// we keep the franceConnect menu only if user is connected with FranceConnect
		isFranceConnectUser = (route: RouteRecordRaw) => {
			return route.meta?.requireFranceConnect
				? !!getToken('franceConnectToken')?.length || !!getToken('notActivatedFcUserToken')?.length
				: true;
		};
	}

	const entries: ComputedRef<Array<NavigationEntry>> = computed(() => {
		let computedRoutes = routes;

		if (accountRoutes) {
			computedRoutes = accountRoutes.children;
			computedRoutes = computedRoutes.filter(isDematAgreementAllowed).filter(isFranceConnectUser);
		}
		return computedRoutes.filter(isNavEntry).map(it => asNavigationEntry(it, activeRoutes.value));
	});

	return { entries };
}
